import React, { useCallback, useEffect, useMemo } from 'react';
import Select, { components } from '@atlaskit/select';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import type { ContextAndValuesOfIssueBeingCreated } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { extractFieldValueMap } from '@atlassian/jira-servicedesk-insight-async-paginated-hook/src/common/utils/extract-field-value-map/index.tsx';
import { usePaginatedAutocomplete } from '@atlassian/jira-servicedesk-insight-async-paginated-hook/src/controllers/index.tsx';
import type { OptionsType } from '@atlassian/jira-servicedesk-insight-async-paginated-hook/src/controllers/types.tsx';
import type { CmdbObjectAttribute } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/attribute.tsx';
import type { CmdbObject } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/cmdb-object.tsx';
import { MAX_OBJECT_COUNT_CUTOFF } from '@atlassian/jira-servicedesk-insight-shared-utils/src/common/constants.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { searchCmdbObjects } from '../services/search-cmdb-object/index.tsx';
import { CmdbFieldError } from './cmdb-field-error/index.tsx';
import { CmdbSelectOption } from './cmdb-select-option/index.tsx';
import { EditMessage, type EditPanelMessage } from './edit-message/index.tsx';
import messages from './messages.tsx';
import { customStyles } from './styled.tsx';

type IssueId = string;

export type Props = {
	issueId?: IssueId;
	autoCompleteUrl: string | null | undefined;
	displayedValues: CmdbObject[] | null;
	selectedValues: CmdbObject[];
	attributesToDisplayInSearch: string[] | undefined;
	multiple: boolean;
	limit: number;
	autoFocus: boolean;
	menuPosition: string | null;
	contextAndValuesOfIssueBeingCreated?: ContextAndValuesOfIssueBeingCreated;
	testId?: string;
	onCloseMenuOnScroll?: (e: Event) => boolean;
	onChange: (value: CmdbObject[]) => void;
	ariaLabel?: string;
	ariaLabelledBy?: string;
	hasValidationErrors?: boolean;
};

export type CmdbObjectOptionProps = {
	id: string;
	label: string;
	avatar: {
		url48: string;
		mediaClientConfig: {
			clientId: string;
			issuer: string;
			fileId: string;
			mediaBaseUrl: string;
			mediaJwtToken: string;
		};
	};
	attributes: CmdbObjectOptionAttribute[];
};

type CmdbObjectOptionAttribute = {
	objectTypeAttribute: {
		name: string;
		label: boolean;
	};
	objectAttributeValues: {
		displayValue: string;
	}[];
};

export const createObjectTransformer =
	(attributesToDisplayInSearch: string[]) =>
	(
		cmdbObject: CmdbObject | CmdbObjectOptionProps,
	): OptionsType<CmdbObject | CmdbObjectOptionProps> => {
		const getDisplayValuesFromAttribute = (
			attribute: CmdbObjectOptionAttribute | CmdbObjectAttribute,
		): string => attribute.objectAttributeValues.map((val) => val.displayValue).join(' ');

		// This label is the string value that @atlaskit/select uses under the hood
		// to determine a 'match' when typing in the select field. Therefore, we need to
		// build a string that includes values for all the attributes marked as
		// 'searchable' within the Insight field configuration.
		const searchableLabel = cmdbObject.attributes
			.map((attribute) => {
				if (
					// @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
					attributesToDisplayInSearch.includes(attribute.objectTypeAttribute.name) ||
					(attributesToDisplayInSearch.includes('Label') &&
						attribute.objectTypeAttribute.label === true)
				) {
					return getDisplayValuesFromAttribute(attribute);
				}
				return undefined;
			})
			.filter(Boolean)
			.join(' ');

		// Label should fall back to the label attribute of the object if it doesn't contain any of the search attributes
		return {
			value: cmdbObject.id,
			label: searchableLabel || cmdbObject.label,
			rawData: cmdbObject,
		};
	};

export const transformDisplayedValues = (
	values: CmdbObject[] | null,
): OptionsType<CmdbObject>[] | null => {
	if (!values) {
		return values;
	}

	return values.map((x) => ({
		label: x.label,
		value: x.objectId,
		rawData: x,
	}));
};

export const CmdbFieldObjectPicker = (props: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const hasConfigError = !props.attributesToDisplayInSearch?.length; // null, undefined or empty is a config error

	const {
		autoCompleteUrl,
		attributesToDisplayInSearch = [],
		multiple = false,
		displayedValues,
		selectedValues = [],
		onChange: propsOnChange,
		issueId,
		limit,
		autoFocus,
		menuPosition = 'absolute',
		onCloseMenuOnScroll,
		contextAndValuesOfIssueBeingCreated,
		testId,
		ariaLabel,
		ariaLabelledBy,
		hasValidationErrors,
	} = props;

	const fetchCmdbObjects = (searchValue: string) => {
		if (typeof autoCompleteUrl === 'string' && autoCompleteUrl) {
			return searchCmdbObjects(
				autoCompleteUrl,
				searchValue,
				issueId,
				extractFieldValueMap(contextAndValuesOfIssueBeingCreated),
			);
		}

		throw new Error('Invalid URL');
	};

	const transformObjectToSelectOption = useMemo(
		() => createObjectTransformer(attributesToDisplayInSearch),
		[attributesToDisplayInSearch],
	);

	const { selectProps, error, totalNumberOfResults } = usePaginatedAutocomplete(
		selectedValues,
		fetchCmdbObjects,
		transformObjectToSelectOption,
		contextAndValuesOfIssueBeingCreated,
	);

	const numberOfSearchResults =
		(totalNumberOfResults ?? 0) >= MAX_OBJECT_COUNT_CUTOFF
			? `${MAX_OBJECT_COUNT_CUTOFF}+`
			: totalNumberOfResults;

	const selectPropsWithLabel = selectProps &&
		Object.keys(selectProps).length && {
			...selectProps,
			options: [
				{
					label: formatMessage(messages.dropdownNumberOfSearchResultsLabel, {
						numberOfSearchResults,
						isLoading: selectProps.isLoading,
					}),
					options: selectProps.options,
				},
			],
		};

	const { showFlag } = useFlagsService();

	const onChange = useCallback(
		(selectedOption: OptionsType<CmdbObject> | OptionsType<CmdbObject>[]) => {
			if (!selectedOption) {
				propsOnChange([]);
				return;
			}

			if (Array.isArray(selectedOption)) {
				propsOnChange(selectedOption.map((x) => x.rawData));
			}

			if (!Array.isArray(selectedOption)) {
				propsOnChange([selectedOption.rawData]);
			}
		},
		[propsOnChange],
	);

	const showGenericErrorFlag = useCallback(() => {
		showFlag({
			type: 'error',
			title: formatMessage(messages.errorTitleCmdbAssets),
			description: formatMessage(messages.errorMessage),
			isAutoDismiss: true,
			messageId: 'servicedesk-cmdb-field-object-picker.ui.show-flag.error.error-title-cmdb-assets',
			messageType: 'transactional',
		});
	}, [formatMessage, showFlag]);

	const responseErrorHandler: { [key: number]: () => void } = useMemo(
		() => ({
			404: () => showGenericErrorFlag(),
			400: () =>
				showFlag({
					type: 'error',
					title: formatMessage(messages.errorTitleCmdbFieldSearch),
					description: formatMessage(messages.errorMessageCmdbFieldSearch),
					isAutoDismiss: true,
					messageId:
						'servicedesk-cmdb-field-object-picker.ui.show-flag.error.error-title-cmdb-field-search',
					messageType: 'transactional',
				}),
		}),
		[formatMessage, showFlag, showGenericErrorFlag],
	);

	useEffect(() => {
		if (error) {
			responseErrorHandler[error.statusCode]
				? responseErrorHandler[error.statusCode]()
				: showGenericErrorFlag();
		}
	}, [error, responseErrorHandler, showGenericErrorFlag]);

	const { Option } = components;

	const customComponents = useMemo(
		() => ({
			// @ts-expect-error - TS7006 - Parameter 'optionProps' implicitly has an 'any' type.
			Option: (optionProps) => (
				<Option {...optionProps}>
					{/* @ts-expect-error - TS2740 - Type '{ label: any; data: any; selectProps: any; attributesToDisplayInSearch: string[]; }' is missing the following properties from type 'CommonProps<OptionTypeBase, boolean, GroupTypeBase<OptionTypeBase>>': clearValue, cx, getStyles, getValue, and 7 more. */}
					<CmdbSelectOption
						label={optionProps.label}
						data={optionProps.data}
						selectProps={optionProps.selectProps}
						attributesToDisplayInSearch={attributesToDisplayInSearch}
					/>
				</Option>
			),
		}),
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[attributesToDisplayInSearch],
	);

	const singleObjectReached = !multiple && selectedValues.length === 1;
	const singleObjectExceeded = !multiple && selectedValues.length > 1;
	const objectLimitReached = selectedValues.length === limit;
	const objectLimitExceeded = selectedValues.length > limit;

	const shouldShowSelect =
		(multiple || selectedValues.length === 0) && selectedValues.length < limit;

	const getMessages = (objectLimit: number): EditPanelMessage[] => {
		const validationMessages: Array<{
			label: 'warning' | 'info';
			messageContent: string;
		}> = [];
		singleObjectExceeded &&
			validationMessages.push({
				label: 'warning',
				messageContent: formatMessage(messages.warningMultipleObjectsMessage),
			});
		objectLimitExceeded &&
			validationMessages.push({
				label: 'warning',
				messageContent: formatMessage(messages.warningObjectLimitExceeded, { objectLimit }),
			});
		singleObjectReached &&
			validationMessages.push({
				label: 'info',
				messageContent: formatMessage(messages.infoSingleObjectMessage),
			});
		objectLimitReached &&
			validationMessages.push({
				label: 'info',
				messageContent: formatMessage(messages.infoObjectLimitReached, { objectLimit }),
			});
		return validationMessages.length > 0 ? [validationMessages[0]] : [];
	};

	return (
		<div data-testid={testId}>
			{hasConfigError && <CmdbFieldError fieldId={contextAndValuesOfIssueBeingCreated?.fieldId} />}
			{!hasConfigError && (displayedValues || shouldShowSelect) && (
				<Select
					// Setting a value of null causes the field to go back to being empty after a selection is made
					value={transformDisplayedValues(displayedValues)}
					isMulti={multiple}
					autoFocus={autoFocus}
					// @ts-expect-error - TS2322 - Type '(selectedOption: OptionsType<CmdbObject> | OptionsType<CmdbObject>[]) => void' is not assignable to type '(value: OptionsType<CmdbObject> | OptionsType<...> | null, action: ActionMeta<...>) => void'.
					onChange={onChange}
					placeholder={formatMessage(messages.searchPlaceholder)}
					components={customComponents}
					isClearable
					menuPlacement="auto"
					// @ts-expect-error - TS2322 - Type 'string | null' is not assignable to type 'MenuPosition | undefined'.
					menuPosition={menuPosition}
					styles={customStyles}
					closeMenuOnScroll={onCloseMenuOnScroll}
					{...selectPropsWithLabel}
					aria-label={ariaLabel}
					aria-labelledby={ariaLabelledBy}
					{...(fg('jsdcloud-17824') ? { isInvalid: hasValidationErrors } : {})}
				/>
			)}
			<EditMessage messages={getMessages(limit)} />
		</div>
	);
};
